import React, { useCallback, useMemo, useState } from 'react';

import {
  arrayToSimpleObj,
  handleMentionData,
  sortMapAuxiliaryFunc,
  supplementNoTimeSingleData,
  supplementSingleData,
  transChannel,
  transLanguage,
  transRegionText,
} from '@/pages/opinion/utils';
import ChartCommonLineBar from './LineAndBar';
import ChartCommonPie from './pie';
import { connect, getLocale, useHistory, useIntl } from 'umi';
import { formatDateFunc, getNeedTime } from '@/pages/opinion/utils/time';
import SentimentChart from './SentimentChart';
import SentimentLineChart from './SentimentLineChart';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import CoordinateChart from './CoordinateChart';
import { transTopicName } from '@/pages/opinion/utils/data';
import { LanguageType } from '@/pages/opinion/types/common';
import { getNewStatsDetailFilter } from '../../../stats/utils';
import BarEchart from './BarEchart';
import StackBarChart from './StackBarChart';
import { channelInfo } from '@op/components/ChannelLabel';
const totalDataList = ['keyword'];
function ChannelsLanguageChart({
  data = {},
  filter,
  type,
  dataType,
  showTitle,
  loading = false,
  height = 300,
  pieLabelAlignTo,
  isVersion = false,
  statsDetailType,
  gameInfoDetail,
  userInfo,
  userInfoLoading,
  wholeGameInfo,
  lang,
  handleStatsDetailOnClick,
  regionType,
  leftTitle,
  rightTitle,
  chartOptions = {},
  defaultSelected,
  finallyDealwithData,
  finallyDealwithTimeline,
  lineProps = {},
  ...restParams
}: {
  data: any;
  filter: any;
  loading?: boolean;
  height?: number;
  showTitle?: boolean;
  pieLabelAlignTo?: string;
  isVersion?: boolean;
  type:
    | 'line'
    | 'pie'
    | 'bar'
    | 'common-bar'
    | 'coordinate'
    | 'sentiment-h-bar'
    | 'sentiment-s-bar'
    | 'sentiment-line'
    | 'stacked-common-bar';
  dataType: 'channel' | 'language' | 'version' | 'keyword' | 'region' | 'topic';
  statsDetailType: string;
  gameInfoDetail?: any;
  userInfo: any;
  userInfoLoading: boolean;
  wholeGameInfo: any;
  lang?: LanguageType;
  handleStatsDetailOnClick?: any;
  regionType?: any;
  leftTitle?: string | JSX.Element;
  rightTitle?: string;
  chartOptions?: { [key: string]: any };
  defaultSelected?: string;
  finallyDealwithData?: (data: any) => any; // 最后处理数据方法
  finallyDealwithTimeline?: (data: any) => any; // 最后处理数据方法
  lineProps: {};
  [key: string]: any;
}) {
  gameInfoDetail = gameInfoDetail || wholeGameInfo;
  const { formatMessage } = useIntl();
  const local = getLocale();
  const history = useHistory();
  const { goPage } = useDrilldownParams();
  const [keywordLanguageMap, setKeywordLanguageMap] = useState({
    origin: {},
    en: {},
    zh: {},
  });

  const dealwithOthersName = (name: string, realName?: string) => {
    if (name === 'Others') {
      return local === 'en-US' ? 'Others' : '其他';
    }

    if (name === 'Unidentified') {
      return local === 'en-US' ? 'Unidentified' : '未区分国家/地区';
    }

    if (name === 'Unlabeled reviews') {
      return local === 'en-US' ? 'Unlabeled reviews' : '未标记的评论';
    }

    return realName || name;
  };

  const transFuncMap = useMemo(() => {
    return {
      channel: (channelName: string) => {
        const { name } = channelInfo(channelName as any);
        return dealwithOthersName(name);
      },
      language: (languageName: string) =>
        dealwithOthersName(languageName, transLanguage(languageName)),
      version: (versionName: string) => dealwithOthersName(versionName),
      keyword: (keywordName: string) => {
        let keywordMap: any = keywordLanguageMap?.[lang as 'origin'];
        return dealwithOthersName(
          keywordName,
          keywordMap?.[keywordName] || keywordName,
        );
      },
      region: (regionName: string) =>
        dealwithOthersName(regionName, transRegionText(regionName)),
      topic: (topicName: string) => {
        if (userInfoLoading) return topicName;
        return dealwithOthersName(
          topicName,
          transTopicName(topicName, gameInfoDetail),
        );
      },
    };
  }, [keywordLanguageMap, formatMessage, userInfoLoading, lang]);
  // 这里依赖的lang不是全局的，而是上级传入的
  const {
    chartData,
    timeArr,
    newArr,
    pieMentionTotal,
    originLegend,
    timelineTotal,
  } = useMemo(() => {
    let pieMentionTotal = 0; // 计算饼状图总声量
    let originLegend: string[] = [];
    let pieLegendData: string[] = []; // 饼状图图例数据
    let newArr: any[] = [];
    let timeArr: string[] = [];
    let {
      mentions,
      timeline_of_mentions,
      sum,
      timeline_of_start_time,
      timeline_of_end_time,
    } = data || {};
    let chartData = data;
    const formatTimeFunc = formatDateFunc(filter?.date_type);

    let { timeline_total, total } = sum || {};
    if (data && mentions?.length > 0) {
      let transKeywordMap = { origin: {}, en: {}, zh: {} };
      const dataMap = (curData: any, isPie: boolean) => {
        return curData.map((item: any) => {
          let name = item?.name;
          if (dataType === 'keyword' && isPie) {
            transKeywordMap['zh'] = {
              ...transKeywordMap['zh'],
              [name]: item.name_cn,
            };
            transKeywordMap['origin'] = {
              ...transKeywordMap['origin'],
              [name]: item.name,
            };
            transKeywordMap['en'] = {
              ...transKeywordMap['en'],
              [name]: item.name_en,
            };
          }
          if (isPie) {
            pieMentionTotal += item?.count || 0;
            pieLegendData.push(name);
            originLegend.push(name);
          }
          if (name === 'youtube_keyword' || name === 'google_keyword')
            item.name = transChannel(name);
          item.value = item?.count || 0;
          return item;
        });
      };
      setKeywordLanguageMap(transKeywordMap);
      if (mentions) {
        chartData.mentions = dataMap(mentions, true);
      }
      if (timeline_of_mentions) {
        chartData.timeline_of_mentions = dataMap(timeline_of_mentions, false);
      }

      chartData.pieMentionTotal = total || pieMentionTotal;
      chartData.pieLegendData = pieLegendData;
      // originLegend originMentions 用于跳转下钻页（源数据）
      chartData.defaultLegend = originLegend;

      let list = [];

      if (timeline_of_start_time) {
        list = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          timeline_of_mentions,
          filter?.date_type,
        );
      } else if (dataType === 'version') {
        list = supplementNoTimeSingleData(timeline_of_mentions).sort(
          (a: any, b: any) => parseInt(b.time) - parseInt(a.time),
        );
      } else {
        list = timeline_of_mentions.sort(
          (a: any, b: any) => parseInt(b.time) - parseInt(a.time),
        );
      }

      if (finallyDealwithData) {
        list = finallyDealwithData(list);
      }

      list?.forEach((v: any) => {
        if (v.count == 0) {
          v.avg_sentiment = null;
          v.negative = 0;
          v.neutral = 0;
          v.positive = 0;
        }
        v.avg = v.avg_sentiment;
      });

      const needTime = getNeedTime(filter?.date_type);

      let mentionData = handleMentionData(
        list,
        isVersion ? false : needTime,
      );

      if (finallyDealwithTimeline) {
        mentionData = finallyDealwithTimeline(mentionData);
      }

      newArr = mentionData.newArr;
      timeArr = mentionData.timeArr;
    }

    if (isVersion) {
      chartData.mentions?.sort(
        (a: any, b: any) =>
          parseInt(b?.name?.split('.').join('')) -
          parseInt(a?.name?.split('.').join('')),
      );
    }

    // 获取排序顺序对象
    let sortByItemMap =
      chartData.mentions
        ?.sort((a: any, b: any) => b.count - a.count)
        ?.reduce(
          (pre: any, cur: any, index: number) => ({
            ...pre,
            [cur.name]:
              cur.name?.toLocaleLowerCase() === 'others' ? 1000 : index,
          }),
          {},
        ) || {};
    // 进行排序
    newArr.sort(sortMapAuxiliaryFunc(sortByItemMap, (item) => item.name));
    chartData.mentions?.sort(
      sortMapAuxiliaryFunc(sortByItemMap, (item) => item.name),
    );
    return {
      chartData,
      timeArr,
      newArr,
      pieMentionTotal,
      originLegend,
      timelineTotal: timeline_total
        ? arrayToSimpleObj(timeline_total, 'value', (item) =>
            formatTimeFunc(item.time),
          )
        : null,
    };
  }, [data, finallyDealwithData]);

  const handleOnClick = useCallback(
    (params: any, eChartsRef: any) => {
      // const curFilter = Object.assign({}, filter);
      // console.log(curFilter, '-------');
      // 版本下的折线图不参与下钻
      if (isVersion && params.seriesType === 'line') return;
      const { location } = history;

      if (location?.pathname === '/opinion/Stats/Detail') {
        handleStatsDetailOnClick?.({
          statsDetailType,
          dataType,
          params,
          regionType,
          chartData,
        });
        return;
      }
      goPage({
        path: '/opinion/Stats/Detail',
        filter: {
          filter: getNewStatsDetailFilter({
            filter,
            dataType,
            params,
            chartData,
            statsDetailType,
          }),
          statsDetailType,
          selected: params?.data?.originName,
          statsDetailChartsData: {
            data,
            dataType,
            isVersion,
            regionType,
            type,
            lang,
          },
          ...(params?.rankBy ? { rankBy: params?.rankBy } : {}),
        },
      });
    },
    [dataType, filter, data, isVersion, type],
  );

  const Element = useMemo(() => {
    const chartComponentMap: any = {
      line: {
        Component: ChartCommonLineBar,
        params: {
          chartData: newArr,
          xAxisData: timeArr,
          filter,
          chartType: 'line',
          timelineTotal: totalDataList.includes(dataType)
            ? null
            : timelineTotal,
          tips: totalDataList.includes(dataType)
            ? `Keyword mentioned frequency`
            : '',
          formatMessage: formatMessage,
          showTitle,
          dataType: 'Mentions',
          pieLabelAlignTo: pieLabelAlignTo || 'edge',
          isVersion,
          leftTitle,
          rightTitle,
          ...lineProps,
        },
      },
      bar: {
        Component: ChartCommonLineBar,
        params: {
          chartData: newArr,
          xAxisData: timeArr,
          filter,
          timelineTotal: timelineTotal,
          tips: dataType === 'topic' ? 'Topic mentioned frequency' : '',
          isTopic: dataType === 'topic',
          chartType: 'bar',
          formatMessage: formatMessage,
          showTitle,
          isVersion,
          dataType: 'Mentions',
        },
      },
      pie: {
        Component: ChartCommonPie,
        params: {
          chartData: chartData.mentions,
          xAxisData: timeArr,
          mentionsTotal: chartData.pieMentionTotal,
        },
      },
      'common-bar': {
        Component: BarEchart,
        params: {
          chartData: chartData.mentions,
          total: chartData.pieMentionTotal,
        },
      },
      'sentiment-h-bar': {
        Component: SentimentChart,
        params: {
          chartData: chartData.mentions,
          filter,
        },
      },
      'sentiment-s-bar': {
        Component: SentimentLineChart,
        params: {
          chartData: chartData.mentions,
          filter,
        },
      },
      'sentiment-line': {
        Component: ChartCommonLineBar,
        params: {
          chartData: newArr.map((item) => ({ ...item, data: item.sentiment })),
          xAxisData: timeArr,
          filter,
          chartType: 'line',
          isVersion,
          formatMessage: formatMessage,
          showTitle,
          dataType: 'Sentiment',
        },
      },
      coordinate: {
        Component: CoordinateChart,
        params: {
          scatterData: chartData.mentions?.map((v: any) => {
            return {
              mentions: v.count,
              sentiment: v.avg_sentiment,
              version: v.name,
              cover: v.cover || gameInfoDetail?.cover,
            };
          }),
          filter,
        },
      },
      'stacked-common-bar': {
        Component: StackBarChart,
        params: {
          data: chartData.mentions,
          filter,
        },
      },
    };

    const { Component, params } = chartComponentMap[type] || {};

    if (!Component) return null;

    return (
      <Component
        height={height}
        loading={loading}
        transFunc={transFuncMap[dataType]}
        handleOnClick={handleOnClick}
        pieLabelAlignTo={pieLabelAlignTo || 'edge'}
        chartOptions={chartOptions}
        defaultSelected={defaultSelected}
        isTopic={dataType === 'topic'}
        lang={lang}
        {...params}
        {...restParams}
      ></Component>
    );
  }, [
    handleOnClick,
    chartData,
    timeArr,
    newArr,
    pieMentionTotal,
    originLegend,
    filter,
    loading,
    type,
    transFuncMap,
  ]);

  return Element;
}

export default connect((state: any) => {
  const { home, opinionCommon, loading } = state;

  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    userInfo: home.userInfo,
    userInfoLoading: loading.effects['home/getUserInfo'],
  };
})(ChannelsLanguageChart);
